import { CapacitorHttp, Capacitor } from '@capacitor/core';

let sessionInitPromise: Promise<void> | null = null;

export async function ensureSession(): Promise<void> {
  if (typeof window === "undefined") return;
  if (!sessionInitPromise) {
    sessionInitPromise = (async () => {
      try {
        const res = await fetch("/api/session", { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          if (data?.token && typeof sessionStorage !== "undefined") {
            sessionStorage.setItem("spa_session_token", data.token);
          }
        }
      } catch (e) {
        // ignore
      } finally {
        sessionInitPromise = null;
      }
    })();
  }
  return sessionInitPromise;
}

/**
 * apiFetch — a thin wrapper around `fetch` that automatically injects the
 * `X-Internal-Api-Key` and `X-SPA-Session` headers, auto-initializes session,
 * and transparently retries on 401.
 */
export async function apiFetch(input: RequestInfo | URL, init: RequestInit = {}, isRetry = false): Promise<Response> {
  let url = typeof input === 'string' ? input : (input instanceof URL ? input.toString() : (input as Request).url);
  const isNative = typeof window !== 'undefined' && Capacitor.isNativePlatform();

  // For API calls in Capacitor, use native HTTP to bypass CORS and set restricted headers
  if (url.startsWith('/api') && isNative) {
    const finalUrl = `https://stockinvestingpro.com${url}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Origin': 'https://stockinvestingpro.com',
      'Referer': 'https://stockinvestingpro.com/',
    };

    if (init.headers) {
      if (init.headers instanceof Headers) {
        init.headers.forEach((v, k) => { headers[k] = v; });
      } else if (Array.isArray(init.headers)) {
        init.headers.forEach(([k, v]) => { headers[k] = v; });
      } else {
        Object.assign(headers, init.headers);
      }
    }

    const apiKey = import.meta.env.VITE_INTERNAL_API_KEY;
    if (apiKey) {
      headers['X-Internal-Api-Key'] = apiKey;
    }

    let data = init.body;
    // CapacitorHttp expects an object for data if it's JSON
    if (typeof data === 'string' && headers['Content-Type']?.includes('application/json')) {
      try {
        data = JSON.parse(data);
      } catch (e) { /* ignore */ }
    }

    try {
      const response = await CapacitorHttp.request({
        url: finalUrl,
        method: (init.method || 'GET').toUpperCase(),
        headers,
        data,
      });

      const responseBody = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
      return new Response(responseBody, {
        status: response.status,
        headers: new Headers(response.headers as any),
      });
    } catch (err) {
      console.error('CapacitorHttp error:', err);
      // Fallback to fetch if native fails
    }
  }

  // Fallback for non-API or non-Capacitor
  if (typeof window !== "undefined" && url.startsWith("/api") && url !== "/api/session") {
    const hasToken = typeof sessionStorage !== "undefined" && sessionStorage.getItem("spa_session_token");
    if (!hasToken) {
      await ensureSession();
    }
  }

  const reqInit = { ...init, credentials: "include" as const };
  const headers = new Headers(reqInit.headers || {});
  const apiKey = import.meta.env.VITE_INTERNAL_API_KEY;
  if (apiKey) {
    headers.set("X-Internal-Api-Key", apiKey);
  }

  if (typeof sessionStorage !== "undefined") {
    const spaToken = sessionStorage.getItem("spa_session_token");
    if (spaToken) {
      headers.set("X-SPA-Session", spaToken);
    }
  }

  reqInit.headers = headers;

  const res = await fetch(input, reqInit);

  // If unauthorized on first web call (e.g. session cookie not yet initialized), initialize session and retry once
  if (res.status === 401 && !isRetry && url.startsWith('/api') && url !== '/api/session') {
    await ensureSession();
    return apiFetch(input, init, true);
  }

  return res;
}
